import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import type { ClientMedium } from '../types/clients';

export type ClientRecord = {
  id: string;
  name: string;
  phone: string;
  status: 'lead' | 'active' | 'past' | 'cold' | null;
  goal: string | null;
  progress_percentage: number | null;
  last_interaction_at: string | null;
  next_reminder_at: string | null;
  updated_at: string | null;
  whatsapp_jid: string | null;
  medium: ClientMedium | null;
};

type RawClientRow = Omit<ClientRecord, 'whatsapp_jid'> & {
  client_whatsapp_links?: { whatsapp_jid: string | null }[] | null;
};

async function fetchClients(): Promise<ClientRecord[]> {
  const selectColumns =
    'id, name, phone, status, goal, progress_percentage, last_interaction_at, next_reminder_at, updated_at, medium, client_whatsapp_links(whatsapp_jid)';

  const runQuery = (withDeletedFilter: boolean) => {
    let query = supabase.from('clients').select(selectColumns).order('updated_at', {
      ascending: false,
    });
    if (withDeletedFilter) {
      query = query.is('deleted_at', null);
    }
    return query;
  };

  let { data, error } = await runQuery(true);

  if (error?.code === '42703') {
    ({ data, error } = await runQuery(false));
  }

  if (error) {
    throw new Error(error.message);
  }

  const rows = (data as RawClientRow[] | null) ?? [];
  return rows.map(({ client_whatsapp_links, ...rest }) => ({
    ...rest,
    whatsapp_jid: client_whatsapp_links?.[0]?.whatsapp_jid ?? null,
  }));
}

export function useClients() {
  return useQuery<ClientRecord[]>({
    queryKey: ['clients'],
    queryFn: fetchClients,
  });
}
